import React from "react";

/**
 * Whether a portal slot currently has children, tracked from the element
 * itself. A ref callback rather than a mount-time getElementById: this zone
 * renders nothing on some routes, so a page reached by client navigation can
 * mount the slot long after the component first rendered, and an observer
 * attached once at mount would never see it.
 */
export function useHasChildren(): [
  (node: HTMLElement | null) => void,
  boolean,
] {
  const [hasChildren, setHasChildren] = React.useState(false);
  const observerRef = React.useRef<MutationObserver | null>(null);
  const ref = React.useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) {
      setHasChildren(false);
      return;
    }
    const sync = () => setHasChildren(node.childNodes.length > 0);
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(node, { childList: true });
    observerRef.current = observer;
  }, []);
  return [ref, hasChildren];
}
